import React from 'react';
import { getPropByPath, setPropByPath } from '../helpers';

const PropEditorSelectOptionNullSpecialValue = '____null____';

type PropContext = string | ((obj: Record<string, unknown>) => Record<string, unknown>);

interface SelectOption {
	label: string;
	value: string | null;
}

interface PropEditorProp {
	key: string;
	label: string;
	type?: 'info' | 'select' | 'multiselect' | 'datetime' | 'file' | 'checkbox' | 'text' | 'password' | 'number' | 'email';
	value?: unknown | ((obj: Record<string, unknown>) => unknown);
	defaultValue?: unknown;
	context?: PropContext;
	disabled?: boolean;
	options?: (string | SelectOption)[];
	accept?: string;
	size?: number;
	placeholder?: string;
	help?: string;
	present?: (value: unknown) => string;
	parse?: (value: string) => unknown;
	render?: (obj: Record<string, unknown>, onChange: (obj: Record<string, unknown>) => void) => React.ReactNode | false;
}

interface PropEditorTool {
	label?: string;
	action?: (obj: Record<string, unknown>) => void;
	render?: () => React.ReactNode;
}

type ToolInput = PropEditorTool | (() => React.ReactNode);

interface PropEditorProps<T = Record<string, unknown>> {
	object?: T;
	props?: PropEditorProp[];
	tools?: ToolInput[];
	onChange: (obj: T) => void;
}

function secretProp(
	prop: PropEditorProp,
	secretsProp: string = 'secrets',
	knownSecretsProp: string = 'knownSecrets'
): PropEditorProp {
	return {
		...prop,
		render: (o: Record<string, unknown>, update: (obj: Record<string, unknown>) => void) => {
			const knownSecrets = o[knownSecretsProp] as string[] | undefined;
			const secrets = o[secretsProp] as Record<string, unknown> | undefined;

			if (!knownSecrets?.includes(prop.key) || secrets?.[prop.key] !== undefined)
				return false;

			return <div><code>********</code> <input type="button" value={`Change ${prop.label}`} onClick={
				() => { update({ ...o, [secretsProp]: { ...(o.secrets as Record<string, unknown>), [prop.key]: '' }}); }
			} /></div>;
		}
	};
}

function PropEditor<T = Record<string, unknown>>({ object: inputObject, props: inputProps, tools: inputTools, onChange }: PropEditorProps<T>) {
	const onChangeRecord = (obj: Record<string, unknown>) => onChange(obj as T);
	let props: PropEditorProp[];
	if (!inputProps) {
		if (inputObject) props = Object.getOwnPropertyNames(inputObject).map(key => ({ key, label: key[0].toUpperCase() + key.slice(1) }));
		else props = [];
	} else {
		props = inputProps;
	}

	let tools: PropEditorTool[] | undefined;
	if (inputTools) {
		tools = inputTools.map(tool => {
			if (typeof tool === 'function')
				return { render: tool };
			else if (tool.action && !tool.render) {
				const toolWithAction = tool;
				return { ...tool, render: () => (<button type="button" onClick={() => toolWithAction.action?.(object)}>{toolWithAction.label}</button>) };
			}
			else
				return tool;
		});
	}

	const getPropValue = (obj: Record<string, unknown>, prop: PropEditorProp): unknown => {
		if (prop.value) {
			if (typeof prop.value === 'function')
				return prop.value(obj);
			else
				return prop.value;
		}

		if (!prop.context)
			return getPropByPath(obj, prop.key);

		if (typeof prop.context === 'function')
			return getPropByPath(prop.context(obj) as Record<string, unknown>, prop.key) ?? prop.defaultValue;

		if (typeof prop.context === 'string')
			return getPropByPath(obj[prop.context] as Record<string, unknown>, prop.key) ?? prop.defaultValue;

		throw new Error(`Invalid context type: ${typeof prop.context}`);
	};

	const getMultiSelectValue = (select: HTMLSelectElement): string[] => {
		const options = select.options;
		const value: string[] = [];

		for (let i = 0; i < options.length; i++) {
			if (options[i].selected) {
				value.push(options[i].value);
			}
		}

		return value;
	};

	const getLocalTime = (date: Date): Date => {
		const local = new Date(date);
		local.setMinutes(date.getMinutes() - date.getTimezoneOffset());
		return local;
	};

	const generateUpdatedObject = (obj: Record<string, unknown>, context: PropContext | undefined, key: string, inputValue: unknown): Record<string, unknown> => {
		let value = inputValue;
		if (value === PropEditorSelectOptionNullSpecialValue)
			value = null;

		if (!context) {
			const result = { ...obj };
			setPropByPath(result, key, value);
			return result;
		}

		if (typeof context === 'function') {
			// Deep clone object
			const clonedObject = JSON.parse(JSON.stringify(obj)) as Record<string, unknown>;
			const clonedContext = context(clonedObject);

			setPropByPath(clonedContext, key, value);

			return clonedObject;
		}

		if (typeof context === 'string') {
			// Don't need to deep clone when context is a string as it can only be a first level property
			const result = { ...obj, [context]: { ...(obj[context] as Record<string, unknown>) } };
			const resultContext = result[context] as Record<string, unknown>;

			setPropByPath(resultContext, key, value);

			return result;
		}

		throw new Error(`Invalid context type: ${typeof context}`);
	};

	const readFileToProp = (obj: Record<string, unknown>, context: PropContext | undefined, key: string, file: File): void => {
		const reader = new FileReader();

		reader.onload = (e) => {
			onChangeRecord(generateUpdatedObject(obj, context, key, e.target?.result));
		};

		reader.readAsText(file);
	};

	const object = (inputObject || {}) as Record<string, unknown>;

	return (
		<form autoComplete="off">
			<table>
				<tbody>
					{
						props.map((prop, propIndex) => (
							<tr key={prop.key + `_${propIndex}`}>
								<td colSpan={prop.type && prop.type === 'info' ? 2 : 1}><span style={{ fontWeight: prop.type && prop.type === 'info' ? 'bold' : 'normal'}}>{prop.label}</span></td>
								<td>
									{
										(prop.type && prop.type === 'info' && <div></div>)
										||
										(prop.render && prop.render(object, onChangeRecord))
										||
										(
											prop.type && prop.type === 'select'
											? (
												<select disabled={prop.disabled} value={String(getPropValue(object, prop) ?? prop.defaultValue ?? '')} onChange={e => onChangeRecord(generateUpdatedObject(object, prop.context, prop.key, e.target.value))}>
													{
														prop.options && prop.options.map(option =>
															option && (
																typeof option === 'string'
																? (<option key={option} value={option}>{option}</option>)
																: (<option key={option.value ?? PropEditorSelectOptionNullSpecialValue} value={option.value ?? PropEditorSelectOptionNullSpecialValue}>{option.label}</option>)
															)
														)
													}
												</select>
											)
											: prop.type && prop.type === 'multiselect'
											? (
												<select disabled={prop.disabled} value={(getPropValue(object, prop) ?? prop.defaultValue ?? []) as string[]} onChange={e => onChangeRecord(generateUpdatedObject(object, prop.context, prop.key, getMultiSelectValue(e.target)))} multiple>
													{
														prop.options && prop.options.map(option =>
															option && (
																typeof option === 'string'
																? (<option key={option} value={option}>{option}</option>)
																: (<option key={option.value ?? ''} value={option.value ?? ''}>{option.label}</option>)
															)
														)
													}
												</select>
											)
											: prop.type && prop.type === 'datetime'
											? (
												<input
													type="datetime-local"
													disabled={prop.disabled}
													value={getLocalTime(new Date(String(prop.present?.(getPropValue(object, prop)) ?? getPropValue(object, prop) ?? prop.defaultValue ?? ''))).toISOString().slice(0, -8)}
													onChange={e => onChangeRecord(generateUpdatedObject(object, prop.context, prop.key, e.target.value))}
												/>
											)
											: prop.type && prop.type === 'file'
											? (
												<input
													type="file"
													disabled={prop.disabled}
													accept={prop.accept || '*'}
													onChange={e => e.target.files?.[0] && readFileToProp(object, prop.context, prop.key, e.target.files[0])}
												/>
											)
											: prop.type && prop.type === 'checkbox'
											? (
												<input
													type="checkbox"
													disabled={prop.disabled}
													checked={Boolean(getPropValue(object, prop) ?? prop.defaultValue ?? false)}
													onChange={e => onChangeRecord(generateUpdatedObject(object, prop.context, prop.key, e.target.checked))}
												/>
											)
											: (
												<input
													type={prop.type || "text"}
													disabled={prop.disabled}
													size={prop.size || undefined}
													placeholder={prop.placeholder || undefined}
													value={String(prop.present?.(getPropValue(object, prop)) ?? getPropValue(object, prop) ?? prop.defaultValue ?? '')}
													onChange={e => onChangeRecord(generateUpdatedObject(object, prop.context, prop.key, prop.parse?.(e.target.value) ?? e.target.value))}
												/>
											)
										)
									}

									{
										prop.help && (
											<div style={{ fontSize: '0.8em', color: '#aaa' }}>
												{prop.help}
											</div>
										)
									}
								</td>
							</tr>
						))
					}

					{
						tools && (
							<tr>
								<td colSpan={2}>
									{
										tools.map((tool, toolIndex) => (
											<React.Fragment key={`t${toolIndex}`}>
												{tool.render?.()}
											</React.Fragment>
										))
									}
								</td>
							</tr>
						)
					}
				</tbody>
			</table>
		</form>
	);
}

export default PropEditor;
export { secretProp };
export type { PropEditorProp, PropEditorTool, PropEditorProps, SelectOption };
